import { z } from 'zod';
import { applyPatch, Operation } from 'fast-json-patch';
import { parse as parsePartialJson } from 'partial-json';
import type {
	ChatCompletionCreateParamsStreaming,
	ChatCompletionMessageParam,
} from 'openai/resources/chat/completions';
import type OpenAI from 'openai';
import {
	DEFAULT_LLM,
	TOOL_NAME_BUILD_DATA_MODEL,
	toEngineName,
} from '@/constants';
import { getOpenAIClient } from '@/singleton-clients';
import { zodToReadableType } from '@/utils/zod-utils';
import { fakerOptions } from './fake-options';
import { buildFakerTypeMethodList } from './faker-type-list';
import {
	toolSchema,
	dataModelDocumentSchema,
	dataModelJsonPatchSchema,
	type BuildDataModelContext,
	type BuildDataModelEngineResult,
	type DataModelDocument,
} from './types';
import { validateAndFixRelationships } from './validate-relationships';
import { devDebug } from '@/utils/dev-debug';

type ToolParams = z.infer<typeof toolSchema>;

/** Max tables per LLM call. Larger models use batch mode. */
const TABLE_BATCH_SIZE = 10;

const tablePlanSchema = z.object({
	tables: z.array(
		z.object({
			tableName: z.string(),
			tableType: z.enum(['fact', 'dimension']),
		}),
	),
});

type TablePlan = z.infer<typeof tablePlanSchema>['tables'];

/** Schema for a batch response — just new tables + optional model-level fields. */
const batchResponseSchema = z.object({
	tables: dataModelDocumentSchema.shape.tables,
	dbName: z.string().optional(),
	summary: z.string().optional(),
});

function documentWithDisplayIds(model: DataModelDocument) {
	return {
		...model,
		tables: model.tables.map((t, id) => ({
			...t,
			id: id.toString(),
			columns: t.columns.map((c, cid) => ({ ...c, id: cid.toString() })),
		})),
	};
}

/** System prompt for the original single-pass JSON Patch mode. */
function buildPatchSystemPrompt(
	types: string[],
	model: DataModelDocument,
): string {
	return `You are a JSON Patch generator for a database schema.
You will generate or update a schema based on the user's prompt.

Here are all the fakerTypes you can use:
${types.join('\n')}

${fakerOptions}

Model Schema:
${zodToReadableType(dataModelDocumentSchema)}

Current Value:
${JSON.stringify(documentWithDisplayIds(model))}

JSON Patch Examples:
- Add a column:
{ "op": "add", "path": "/tables/0/columns/0", "value": { ... }]
- Update a column type:
{ "op": "replace", "path": "/tables/0/columns/1/type", "value": "text" }
- Remove a column:
{ "op": "remove", "path": "/tables/0/columns/2" }
- Add a table at the end of the existing array (index 4):
{ "op": "add", "path": "/tables/4", "value": { ... } }
- Replace the dbName:
{ "op": "replace", "path": "/dbName", "value": "Model Name" }
- Replace the summary:
{ "op": "replace", "path": "/summary", "value": "Model Summary" }

Response Format:
{ "patches": [{ "op": "add | replace | remove", "path": "/<path_to_the_value>", "value": <value> }] }

IMPORTANT:
- Operations to arrays must always specify the correct index in the path. Example: path: \`/tables/0\`.
- Arrays are 0 indexed. When removing, make sure the index is smaller than the length of the array.
- Make sure dbName, tables, summary are not blank.
- If tableType is "dimension", set rowCount to 50 or less. If tableType is "fact", set rowCount to 1000 or more.
- When generating multiple tables, generate a separate patch for each table and always include the index in the path.
- When a dimension table is renamed, update the corresponding referenceTo.table in the fact table.
- When a column of a dimension table is renamed, update the corresponding referenceTo.column in the fact table.
`;
}

/** System prompt for batch mode — LLM returns table objects, not patches. */
function buildBatchSystemPrompt(
	types: string[],
	model: DataModelDocument,
): string {
	const existingTableSummary = model.tables.map((t) => ({
		tableName: t.tableName,
		tableType: t.tableType,
		primaryKey: t.columns.find((c) => c.isPrimaryKey)?.columnName,
	}));

	return `You are a database schema generator using Ralph Kimball's dimensional modeling methodology.

Here are all the fakerTypes you can use:
${types.join('\n')}

${fakerOptions}

Table schema:
${zodToReadableType(dataModelDocumentSchema.shape.tables.element)}

Existing tables already in the model (DO NOT include these in your response):
${JSON.stringify(existingTableSummary)}

Response format — return ONLY the new tables for this batch:
{
  "tables": [ ...new table objects... ],
  "dbName": "...",   (include only if not yet set)
  "summary": "..."   (include only if not yet set)
}

RULES:
- Each table must have: tableName (PascalCase), tableType ("fact"|"dimension"), rowCount, columns.
- rowCount: ≤50 for dimension tables, ≥1000 for fact tables.
- Every column needs: columnName, isPrimaryKey, isForeignKey, type ("numeric"|"text"|"datetime"), fakerType, fakerOptions (optional), referenceTo (if isForeignKey).
- For fact table foreign key columns, set referenceTo to reference the primary key of the corresponding dimension table.
- Do NOT repeat or modify any existing table. Only return the tables explicitly listed in the batch.
`;
}

/** Extracts an explicit table count from the prompt (e.g. "50 tables" → 50). */
function extractRequestedTableCount(prompt: string): number | null {
	const match = prompt.match(/\b(\d+)\s*[-\s]?\s*table/i);
	return match ? parseInt(match[1], 10) : null;
}

/** Planning call: returns list of all tables to create. Returns [] on any failure. */
async function fetchTablePlan(
	prompt: string,
	openAIClient: OpenAI,
	activeSessionId?: string,
): Promise<TablePlan> {
	const requestedCount = extractRequestedTableCount(prompt);
	const countInstruction = requestedCount
		? `CRITICAL: The user has explicitly requested EXACTLY ${requestedCount} tables. You MUST return exactly ${requestedCount} entries — no more, no fewer. Expand with granular sub-domain dimensions (Region, Currency, ShiftType, UnitOfMeasure, etc.) to reach the exact count.`
		: '';

	try {
		const response = await openAIClient.chat.completions.create({
			model: DEFAULT_LLM,
			temperature: 0,
			response_format: { type: 'json_object' },
			messages: [
				{
					role: 'system',
					content: `You are a database schema planner using Ralph Kimball's dimensional modeling methodology.
Given a data model description, list ALL tables that need to be created.
Return JSON: { "tables": [{ "tableName": "string", "tableType": "fact" | "dimension" }] }
${countInstruction}
Rules:
- Use PascalCase for all table names.
- List dimension tables before fact tables.
- Every table name must be unique.`,
				},
				{ role: 'user', content: prompt },
			],
			metadata: activeSessionId
				? { refSessionId: activeSessionId }
				: undefined,
		});
		const content = response.choices[0].message.content ?? '{}';
		const parsed = tablePlanSchema.safeParse(JSON.parse(content));
		return parsed.success ? parsed.data.tables : [];
	} catch {
		return [];
	}
}

/**
 * Generates one batch of tables and appends them to `model.tables`.
 * Uses direct table objects instead of JSON Patch to avoid index confusion.
 */
type CommonOpts = {
	tools: [];
	temperature: 0;
	tool_choice: 'none';
	model: string;
	response_format: { type: 'json_object' };
	metadata?: { refSessionId: string };
};

async function runTableBatch(
	batch: TablePlan,
	batchIndex: number,
	totalBatches: number,
	originalPrompt: string,
	model: DataModelDocument,
	openAIClient: OpenAI,
	context: Pick<
		BuildDataModelContext,
		'onDocumentUpdate' | 'activeSessionId'
	>,
	types: string[],
	commonOpts: CommonOpts,
): Promise<void> {
	const batchNames = batch
		.map((t) => `${t.tableName} (${t.tableType})`)
		.join(', ');

	const userPrompt = `Original request: ${originalPrompt}

Batch ${batchIndex + 1} of ${totalBatches} — generate ONLY these tables:
${batchNames}

The model already has ${
		model.tables.length
	} tables. Do NOT include any of them in your response — only return the ${
		batch.length
	} tables listed above.`;

	try {
		const response = await openAIClient.chat.completions.create({
			...commonOpts,
			stream: false as const,
			messages: [
				{
					role: 'system',
					content: buildBatchSystemPrompt(types, model),
				},
				{ role: 'user', content: userPrompt },
			],
		});

		const content = response.choices[0].message.content ?? '{}';
		const parsed = batchResponseSchema.safeParse(JSON.parse(content));

		if (!parsed.success) {
			console.warn(
				`Batch ${batchIndex + 1} response failed validation`,
				parsed.error,
			);
			return;
		}

		const newTables = parsed.data.tables;
		devDebug('toolCall', {
			tool: 'runTableBatch',
			phase: 'batchAppend',
			batchIndex,
			newTableCount: newTables.length,
			expectedBatchSize: batch.length,
		});

		model.tables = [...model.tables, ...newTables];
		if (parsed.data.dbName && !model.dbName)
			model.dbName = parsed.data.dbName;
		if (parsed.data.summary && !model.summary)
			model.summary = parsed.data.summary;

		context.onDocumentUpdate?.({ ...model });
	} catch (err) {
		console.warn(`Batch ${batchIndex + 1} failed`, err);
	}
}

/**
 * Runs one streaming JSON Patch pass (single-pass mode), applies patches to `model` in place.
 */
async function runStreamingPass(
	messages: ChatCompletionMessageParam[],
	model: DataModelDocument,
	openAIClient: OpenAI,
	context: Pick<
		BuildDataModelContext,
		'onDocumentUpdate' | 'onStreamHistory' | 'activeSessionId'
	>,
	opts: Omit<ChatCompletionCreateParamsStreaming, 'messages' | 'stream'>,
): Promise<void> {
	const { onDocumentUpdate, onStreamHistory } = context;

	const streamOpts: ChatCompletionCreateParamsStreaming = {
		...opts,
		messages,
		stream: true,
	};
	onStreamHistory?.({ kind: 'request', opts: streamOpts });

	let lastPatch = 0;
	let responseSnapshot = '';
	let patches: z.infer<typeof dataModelJsonPatchSchema>[] = [];

	const stream = openAIClient.beta.chat.completions
		.stream(streamOpts)
		.on('content.delta', ({ delta }) => {
			responseSnapshot += delta;
			try {
				patches = parsePartialJson(responseSnapshot)?.patches || [];
			} catch {
				// ignore partial parse errors while streaming
			}
			const len = patches?.length || 0;
			while (lastPatch < len - 1) {
				const rawPatch = patches[lastPatch];
				lastPatch++;
				const patchResult =
					dataModelJsonPatchSchema.safeParse(rawPatch);
				if (!patchResult.success) {
					console.warn(
						'Skipping invalid patch at index',
						lastPatch - 1,
						patchResult.error,
					);
					continue;
				}
				try {
					const { newDocument } = applyPatch(
						{
							dbName: model.dbName,
							tables: model.tables,
							summary: model.summary,
						},
						[
							patchResult.data,
						] as unknown as ReadonlyArray<Operation>,
						undefined,
						false,
					);
					model.dbName = newDocument.dbName;
					model.tables = newDocument.tables;
					model.summary = newDocument.summary;
					onDocumentUpdate?.({ ...model });
				} catch (err) {
					console.warn(
						'applyPatch failed at index',
						lastPatch - 1,
						patchResult.data,
						err,
					);
				}
			}
		});

	await stream.done();
	const finalChatCompletion = await stream.finalChatCompletion();
	// message.content is typed `string | null` — coalesce so we never push a
	// null assistant message into the stream history.
	const finalContent = finalChatCompletion.choices[0].message.content ?? '';

	const finalPatch =
		Array.isArray(patches) && patches.length > 0
			? patches[patches.length - 1]
			: undefined;

	if (finalPatch) {
		const finalPatchResult = dataModelJsonPatchSchema.safeParse(finalPatch);
		if (finalPatchResult.success) {
			try {
				const patched = applyPatch<DataModelDocument>(
					{
						dbName: model.dbName,
						tables: model.tables,
						summary: model.summary,
					},
					[
						finalPatchResult.data,
					] as unknown as ReadonlyArray<Operation>,
				);
				const { newDocument } = patched;
				model.dbName = newDocument.dbName;
				model.tables = newDocument.tables;
				model.summary = newDocument.summary;
			} catch (err) {
				console.warn(
					'applyPatch failed for final patch',
					finalPatchResult.data,
					err,
				);
			}
		} else {
			console.warn(
				'Final patch failed validation, skipping',
				finalPatchResult.error,
			);
		}
	} else {
		console.warn('No patches generated');
	}

	onStreamHistory?.({
		kind: 'complete',
		opts: streamOpts,
		messages: [...messages, { role: 'assistant', content: finalContent }],
		result: { ...model },
	});
}

const USAGE_INSTRUCTIONS = `Logical data model schema generated by the assistant. However, physical data source NOT created yet
and thus visualizations and analytics cannot be created yet.
Please ask the user to use the "Create Data Model" button to load the model into Sisense before using it.`;

/**
 * Generates or updates a data model.
 *
 * Small models (≤ TABLE_BATCH_SIZE tables): single streaming JSON Patch pass.
 * Large models (> TABLE_BATCH_SIZE tables): planning call + N direct-append batches,
 * each asking the LLM to return only the new tables for that batch.
 */
export async function buildDataModelEngine(
	params: Pick<ToolParams, 'prompt'>,
	context: BuildDataModelContext,
): Promise<BuildDataModelEngineResult> {
	const { prompt } = params;
	const { toolCallId, initialDocument, onDocumentUpdate } = context;

	devDebug('engineCall', {
		engine: toEngineName(TOOL_NAME_BUILD_DATA_MODEL),
		toolCallId,
	});

	const types = buildFakerTypeMethodList();
	const model: DataModelDocument = structuredClone(initialDocument);
	const openAIClient = context.openAIClient ?? getOpenAIClient();

	const commonOpts: CommonOpts = {
		tools: [],
		temperature: 0,
		tool_choice: 'none',
		model: DEFAULT_LLM,
		response_format: { type: 'json_object' },
		metadata: context.activeSessionId
			? { refSessionId: context.activeSessionId }
			: undefined,
	};

	// Planning call to decide single-pass vs batch
	const plan = await fetchTablePlan(
		prompt,
		openAIClient,
		context.activeSessionId,
	);

	if (plan.length <= TABLE_BATCH_SIZE) {
		// Single-pass: stream JSON Patch operations (original behaviour)
		const requestedCount = extractRequestedTableCount(prompt);
		const countHint = requestedCount
			? `\n\nIMPORTANT: The user wants EXACTLY ${requestedCount} tables. Generate all of them.`
			: '';
		const messages: ChatCompletionMessageParam[] = [
			{ role: 'system', content: buildPatchSystemPrompt(types, model) },
			{ role: 'user', content: prompt + countHint },
		];
		await runStreamingPass(
			messages,
			model,
			openAIClient,
			context,
			commonOpts,
		);
	} else {
		// Batch mode: dimensions first, then facts; LLM returns table objects directly
		const ordered = [
			...plan.filter((t) => t.tableType === 'dimension'),
			...plan.filter((t) => t.tableType === 'fact'),
		];
		const batches: TablePlan[] = [];
		for (let i = 0; i < ordered.length; i += TABLE_BATCH_SIZE) {
			batches.push(ordered.slice(i, i + TABLE_BATCH_SIZE));
		}

		for (let i = 0; i < batches.length; i++) {
			await runTableBatch(
				batches[i],
				i,
				batches.length,
				prompt,
				model,
				openAIClient,
				context,
				types,
				commonOpts,
			);
		}

		// Fire stream history once with final state
		context.onStreamHistory?.({
			kind: 'complete',
			opts: { ...commonOpts, messages: [], stream: true },
			messages: [],
			result: { ...model },
		});
	}

	model.tables = validateAndFixRelationships(model.tables);
	onDocumentUpdate?.(model);

	return {
		dataSourceTitle: model.dbName,
		summary: model.summary,
		modelId: toolCallId,
		usageInstructions: USAGE_INSTRUCTIONS,
	};
}
